import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import supabase from '../supabaseClient';
import useVenueSetupMode from '../hooks/useVenueSetupMode';
import LoaderScreen from './LoaderScreen';

// Guards children so only users with a non-empty Users.venue_owner can access
//
// Setup-mode bypass (mirrors CommunityRoute): when the venue draft is
// present in sessionStorage, there is no Supabase session to verify.
// The user is in their FE-only setup flow; the layout / sidebar /
// page components handle the locked-tab + Welcome affordances. Doing
// the auth round-trip here would either flash a spinner then redirect
// to /venue-owner/login (no session) or stall while the request resolves.
const VenueOwnerRoute = ({ children }) => {
  const { hasDraftVenue } = useVenueSetupMode();
  const [isAllowed, setIsAllowed] = useState(null);

  useEffect(() => {
    if (hasDraftVenue) return;

    const check = async () => {
      try {
        const { data: userResp } = await supabase.auth.getUser();
        const user = userResp?.user;
        if (!user) {
          setIsAllowed(false);
          return;
        }

        const { data: profile, error } = await supabase
          .from('Users')
          .select('venue_owner')
          .eq('user_id', user.id)
          .single();

        if (error || !profile || !String(profile.venue_owner || '').trim()) {
          setIsAllowed(false);
          return;
        }

        setIsAllowed(true);
      } catch (e) {
        console.error('VenueOwnerRoute error', e);
        setIsAllowed(false);
      }
    };
    check();
  }, [hasDraftVenue]);

  // Setup-mode bypass — render the inner layout immediately, no spinner,
  // no auth round-trip. Returns AFTER the hooks above so the rules of
  // hooks stay satisfied if hasDraftVenue flips later.
  if (hasDraftVenue) {
    return children;
  }

  if (isAllowed === null) return <LoaderScreen subtitle="Verifying venue access" />;
  return isAllowed ? children : <Navigate to="/venue-owner/login" />;
};

export default VenueOwnerRoute;


